import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BigNumber, ethers } from 'ethers';
import { rollChallenge, findChallenge } from '../utils/rollChallenge';
import ChallengeDisplay from './ChallengeDisplay';
import { getRandomNumberInRange } from '../lib/randomItem';
import { getEthersSigner } from "../utils/ethersProvider";
import { getPreferences, canAcceptChallenge } from '../utils/preferencesUtils';
import {
  PageContainer,
  ColumnContainer,
  HomeContainer,
  Button,
  ChallengeHeader,
  MainChallenge,
  FlexLink,
  SectionHeader,
  BrowseButtonContainer,
  Image,
  SloganText,
  ExternalButton,
  LoadingOverlay,
  RouletteWheel,
  LoadingText,
  DialogBox,
  DialogTitle,
  DialogMessage,
  DialogButton,
  DialogButtonRow,
  ModalOverlay
} from './index';
import { abis, addresses } from '@my-app/contracts';
// import logo from '../assets/logo512.png';
import logo from '../assets/chaoshead.gif';

const HomePage = ({ config, walletConnected, provider }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialChallengeId = location.state?.challengeId || null;
  const currentChallengeIdRef = useRef(initialChallengeId); // Ref to track current challenge ID

  const beachheadContract = useMemo(() => new ethers.Contract(addresses.beachhead, abis.Beachhead, provider), [provider]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [showNoMatchDialog, setShowNoMatchDialog] = useState(false);
  const [featureChallenge, setFeatureChallenge] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchWindow] = useState(50);
  const [blocksSearched, setBlocksSearched] = useState(100);
  const [lastSearchBlock, setLastSearchBlock] = useState(null);
  const [lastSearchBlockEnemy, setLastSearchBlockEnemy] = useState('Terminid');

  const fetchFeatureChallenge = useCallback(async () => {
    try {
      setIsLoading(true);
      const blockNumber = await provider.getBlockNumber();
      let userAddress = ethers.constants.AddressZero;

      if (walletConnected) {
        const signer = await getEthersSigner(config);
        if (signer) {
          userAddress = await signer.getAddress();
        } else {
          console.error("Failed to get signer");
        }
      }

      const activeChallengeIds = await beachheadContract.getActiveChallenges();
      let featureChallenge;
      if (activeChallengeIds.length > 0) {
        const activeChallenges = await Promise.all(activeChallengeIds.map(async (id) => {
          return await rollChallenge(provider, id.toNumber(), userAddress);
        }));
        const validChallenges = activeChallenges.filter(challenge => challenge.stats.expireBlock > blockNumber);
        if (validChallenges.length > 0) {
          featureChallenge = validChallenges.reduce((prev, current) => (prev.stats.pot.gt(current.stats.pot) ? prev : current), { stats: { pot: BigNumber.from(0) } });

          if (featureChallenge.stats.pot.eq(0)) {
            featureChallenge = validChallenges.reduce((prev, current) => (prev.stats.total.gt(current.stats.total) ? prev : current), { stats: { total: BigNumber.from(0) } });
          }
        }
      }

      if (!featureChallenge || (featureChallenge.stats.pot.eq(0) && featureChallenge.stats.total.eq(0))) {
        featureChallenge = await rollChallenge(provider, blockNumber, userAddress);
      }

      setFeatureChallenge(featureChallenge);
    } catch (error) {
      console.error('Error fetching feature challenge:', error);
    } finally {
      setIsLoading(false);
    }
  }, [config, provider, walletConnected, beachheadContract]);

  const handleRNGRouletteClick = useCallback(async (enemy, startBlock = null, searchWindow = 50) => {
    try {
      setIsSearching(true);
      setShowNoMatchDialog(false);

      const blockNumber = await provider.getBlockNumber();
      const randomBlockNumber = startBlock !== null ? startBlock : blockNumber - getRandomNumberInRange(150, 1000);

      // Reset blocksSearched if this is a new search
      if (startBlock === null) {
        setBlocksSearched(searchWindow);
      }

      // Get user preferences
      const preferences = getPreferences();
      const searchCriteria = { mission: { enemy } };

      let challenge = null;
      let currentBlock = randomBlockNumber;
      let searched = 0;

      while (!challenge && searched < searchWindow) {
        const potentialChallenge = await findChallenge(provider, currentBlock, 1, searchCriteria);
        if (potentialChallenge && canAcceptChallenge(potentialChallenge, preferences)) {
          challenge = potentialChallenge;
        } else {
          currentBlock--;
          searched++;
        }
      }

      if (challenge) {
        navigate(`/helldivers2/challenge/${challenge.id}`);
      } else {
        if (startBlock !== null) {
          setBlocksSearched(prev => prev + searchWindow);
        }
        setShowNoMatchDialog(true);
        setLastSearchBlock(currentBlock);
      }
      setLastSearchBlockEnemy(enemy);
    } catch (error) {
      setShowNoMatchDialog(true);
    } finally {
      setIsSearching(false);
    }
  }, [provider, navigate]);

  const handleTwoHourClick = useCallback(async () => {
    try {
      const blockNumber = await provider.getBlockNumber();
      const randomBlockNumber = blockNumber - getRandomNumberInRange(39600, 40000);
      navigate(`/helldivers2/browse/${randomBlockNumber}`);
    } catch (error) {
      console.error('Error navigating to two hour challenge:', error);
    }
  }, [provider, navigate]);

  const handleRouletteClick = useCallback(async () => {
    try {
      const blockNumber = await provider.getBlockNumber();
      const randomBlockNumber = blockNumber + getRandomNumberInRange(150, 300);
      navigate(`/helldivers2/challenge/${randomBlockNumber}`);
    } catch (error) {
      console.error('Error navigating to random challenge:', error);
    }
  }, [provider, navigate]);

  useEffect(() => {
    fetchFeatureChallenge();
  }, [fetchFeatureChallenge]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading || !featureChallenge) {
    return (
      <PageContainer>
        <ChallengeHeader>Loading...</ChallengeHeader>
      </PageContainer>
    );
  }

  const uniswapUrl = `https://app.uniswap.org/swap?theme=dark&inputCurrency=ETH&outputCurrency=${addresses.beachheadToken}&chain=base`;
  const uniswapUsdcUrl = `https://app.uniswap.org/swap?theme=dark&inputCurrency=ETH&outputCurrency=${addresses.usdc}&chain=base`;

  const Content = (
    <ColumnContainer>
      <Image src={logo} alt="Logo" />
      <SloganText>Vera Libertas!</SloganText>
      <SectionHeader>Chaos Roulette</SectionHeader>
      <BrowseButtonContainer>
        <Button onClick={() => handleRNGRouletteClick('Terminid')}>Terminid Roulette</Button>
        <Button onClick={() => handleRNGRouletteClick('Automaton')}>Automaton Roulette</Button>
        <Button onClick={() => handleRNGRouletteClick('Illuminate')}>Illuminate Roulette</Button>
        <Button onClick={handleTwoHourClick}>Two Hour Challenge</Button>
        <Button onClick={handleRouletteClick}>Chaos Roulette</Button>
        <Button onClick={() => navigate('/helldivers2/preferences')}>Preferences</Button>
      </BrowseButtonContainer>
      <SectionHeader>Browse Chaos Orders</SectionHeader>
      <BrowseButtonContainer>
        <Button onClick={() => navigate('/helldivers2/browse')}>All Chaos Orders</Button>
        <Button onClick={() => navigate('/helldivers2/list?set=active')}>Active Orders</Button>
        <Button onClick={() => navigate('/helldivers2/list?set=mine')}>My Orders</Button>
      </BrowseButtonContainer>
      <SectionHeader>Navigation</SectionHeader>
      <BrowseButtonContainer>
        <Button onClick={() => navigate('/helldivers2/judge')}>Judge</Button>
        <ExternalButton href={uniswapUsdcUrl} target="_blank" rel="noopener noreferrer">
          Buy/Sell USDC
        </ExternalButton>
        <ExternalButton href={uniswapUrl} target="_blank" rel="noopener noreferrer">
          Buy/Sell BCHD
        </ExternalButton>
        <Button onClick={() => navigate('/about')}>About</Button>
        <ExternalButton href="https://discord.gg/P7hD7g2rM9" target="_blank" rel="noopener noreferrer">
          Community
        </ExternalButton>
        <ExternalButton href="https://www.youtube.com/@TheBeachhead" target="_blank" rel="noopener noreferrer">
          YouTube
        </ExternalButton>
      </BrowseButtonContainer>
      <SectionHeader>Priority Chaos Order</SectionHeader>
      <FlexLink to={`/helldivers2/challenge/${featureChallenge.id}`} state={{ challengeId: currentChallengeIdRef.current }}>
        <MainChallenge>
          <ChallengeDisplay challenge={featureChallenge} displayAccept={false} />
        </MainChallenge>
      </FlexLink>
    </ColumnContainer>
  );

  return (
    <PageContainer>
      {isMobile ? Content : <HomeContainer>{Content}</HomeContainer>}
      {isSearching && (
        <LoadingOverlay>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <RouletteWheel />
            <LoadingText>
              Searching for Chaos Orders matching your preferences...
            </LoadingText>
          </div>
        </LoadingOverlay>
      )}
      {showNoMatchDialog && (
        <>
          <ModalOverlay onClick={() => setShowNoMatchDialog(false)}>
            <DialogBox onClick={e => e.stopPropagation()}>
              <DialogTitle>No Matching Chaos Order Found</DialogTitle>
              <DialogMessage>
                We couldn't find a Chaos Order that matches your preferences within the last {blocksSearched} blocks.<br />
                Try adjusting your preferences and attempt again, or continue searching the next {searchWindow} blocks.
              </DialogMessage>
              <DialogButtonRow>
                <DialogButton onClick={() => setShowNoMatchDialog(false)}>Close</DialogButton>
                <DialogButton onClick={() => {
                  setShowNoMatchDialog(false);
                  navigate('/helldivers2/preferences');
                }}>Preferences</DialogButton>
                <DialogButton onClick={() => {
                  setShowNoMatchDialog(false);
                  handleRNGRouletteClick(lastSearchBlockEnemy, lastSearchBlock, searchWindow);
                }}>Continue</DialogButton>
              </DialogButtonRow>
            </DialogBox>
          </ModalOverlay>
        </>
      )}
    </PageContainer>
  );
};

export default HomePage;
